/**
 * Forgot Password Page - Reconflux
 *
 * Request a password reset email.
 * "We'll help you regain access to your sanctuary."
 */

import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { Trans, useTranslation } from 'react-i18next'
import { Logo } from '../../components/ui/Logo'
import { useAuth } from '../../providers/AuthProvider'
import { marketingHomeUrl } from '../../lib/marketingSite'

export function ForgotPasswordPage() {
  const { t, i18n } = useTranslation('auth')
  const { resetPassword } = useAuth()

  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsLoading(true)

    try {
      const { error } = await resetPassword(email)

      if (error) {
        setError(error.message)
        return
      }

      setSuccess(true)
    } catch {
      setError(t('errors.unexpected'))
    } finally {
      setIsLoading(false)
    }
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-dvh bg-surface-page flex flex-col">
        <header className="p-6">
          <Logo href={marketingHomeUrl(i18n.resolvedLanguage)} />
        </header>

        <main className="flex-1 flex items-center justify-center px-4">
          <div className="w-full max-w-md text-center">
            {/* Success icon */}
            <div className="w-16 h-16 bg-accent/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg
                className="w-8 h-8 text-accent"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            </div>

            <h1 className="text-2xl font-semibold text-ink mb-2">
              {t('forgotPassword.success.title')}
            </h1>
            <p className="text-ink-subtle mb-6">
              <Trans
                t={t}
                i18nKey="forgotPassword.success.body"
                values={{ email }}
                components={{ strong: <strong className="text-ink" /> }}
              />
            </p>

            <p className="text-sm text-ink-muted mb-6">
              <Trans
                t={t}
                i18nKey="forgotPassword.success.retry"
                components={{
                  retry: (
                    <button
                      onClick={() => setSuccess(false)}
                      className="text-accent hover:text-accent-muted transition-colors"
                    />
                  ),
                }}
              />
            </p>

            <Link
              to="/login"
              className="inline-flex items-center gap-2 text-accent hover:text-accent-muted transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              {t('forgotPassword.success.returnToSignIn')}
            </Link>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="min-h-dvh bg-surface-page flex flex-col">
      {/* Header */}
      <header className="p-6">
        <Logo href={marketingHomeUrl(i18n.resolvedLanguage)} />
      </header>

      {/* Main content */}
      <main className="flex-1 flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          {/* Back link */}
          <Link
            to="/login"
            className="inline-flex items-center gap-2 min-h-11 md:min-h-0 text-ink-subtle hover:text-ink mb-6 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            {t('forgotPassword.back')}
          </Link>

          {/* Header text */}
          <div className="mb-8">
            <h1 className="text-2xl font-semibold text-ink mb-2">
              {t('forgotPassword.title')}
            </h1>
            <p className="text-ink-subtle text-sm">
              {t('forgotPassword.subtitle')}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Error message */}
            {error && (
              <div className="bg-danger/10 border border-danger/30 rounded-lg px-4 py-3">
                <p className="text-danger text-sm">{error}</p>
              </div>
            )}

            {/* Email field */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm text-ink-secondary mb-1.5"
              >
                {t('forgotPassword.emailLabel')}
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                autoFocus
                className="w-full px-4 py-3 bg-surface-raised border border-edge/50 rounded-lg
                  text-ink placeholder-ink-muted
                  focus:outline-none focus:border-accent/50 focus:ring-1 focus:ring-accent/30
                  transition-colors"
                placeholder={t('forgotPassword.emailPlaceholder')}
              />
            </div>

            {/* Submit button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 bg-accent text-accent-contrast font-medium rounded-lg
                hover:bg-accent-hover focus:outline-none focus:ring-2 focus:ring-accent/50 focus:ring-offset-2 focus:ring-offset-surface-page
                disabled:opacity-50 disabled:cursor-not-allowed
                transition-all"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  {t('forgotPassword.submitting')}
                </span>
              ) : (
                t('forgotPassword.submit')
              )}
            </button>
          </form>
        </div>
      </main>
    </div>
  )
}
